// Simple email validation functions
const validateEmailFormat = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Log failed email attempts
export const logFailedEmail = async (recipientEmail, reason, appointmentId) => {
  try {
    // Store locally since Amplify doesn't have this endpoint
    const failedEmails = JSON.parse(localStorage.getItem('failedEmails') || '[]');
    failedEmails.push({
      recipientEmail,
      reason,
      appointmentId,
      timestamp: new Date().toISOString()
    });
    localStorage.setItem('failedEmails', JSON.stringify(failedEmails));
    console.log('📝 Failed email logged locally:', { recipientEmail, reason, appointmentId });
  } catch (error) {
    console.error('Failed to log email error:', error);
  }
};

// Send booking confirmation email (dummy function - no actual email sent)
export const sendBookingConfirmationEmail = async (recipientEmail, appointmentDetails) => {
  console.log('📧 Booking confirmation email skipped (only completion emails are sent)');
  return { success: true, message: 'Booking confirmation email skipped' };
};

// Send completion email only when status changes to "Completed"
export const sendStatusChangeEmail = async (recipientEmail, adminEmail, appointmentDetails, newStatus, oldStatus) => {
  try {
    // Only send email when status changes to "Completed"
    if (newStatus !== 'Completed') {
      console.log('📧 No email sent - status is not "Completed"');
      return { success: true, message: 'No email needed for this status change' };
    }

    // Validate inputs
    if (!recipientEmail) {
      throw new Error('Recipient email is required');
    }
    
    if (!appointmentDetails) {
      throw new Error('Appointment details are required');
    }

    if (!validateEmailFormat(recipientEmail)) {
      throw new Error(`Invalid email format: ${recipientEmail}`);
    }

    // Ensure tokenNumber exists
    const tokenNumber = appointmentDetails?.tokenNumber || appointmentDetails?.id || 'N/A';
    
    const emailTemplate = createCompletionEmailTemplate({ ...appointmentDetails, tokenNumber }, adminEmail);
    
    const emailData = {
      to: recipientEmail,
      replyTo: adminEmail || 'noreply@amazon.com',
      from: adminEmail || 'noreply@amazon.com',
      subject: `✅ Photo Session Complete - Token ${tokenNumber}`,
      html: emailTemplate,
      text: `Your photo session is complete. Token: ${tokenNumber}`
    };

    console.log('📧 COMPLETION EMAIL WILL BE SENT:', {
      to: recipientEmail,
      from: adminEmail,
      token: tokenNumber,
      emailData
    });
    
    // Store email in localStorage for verification
    const sentEmails = JSON.parse(localStorage.getItem('sentEmails') || '[]');
    sentEmails.push({
      timestamp: new Date().toISOString(),
      type: 'completion',
      to: recipientEmail,
      from: adminEmail,
      subject: emailData.subject,
      token: tokenNumber
    });
    localStorage.setItem('sentEmails', JSON.stringify(sentEmails));
    
    try {
      console.log('📧 Sending email via direct API (Amplify bypass):', {
        endpoint: 'https://qq06of66f7.execute-api.us-east-1.amazonaws.com/prod/send-booking-email',
        emailData
      });
      
      const response = await fetch('https://qq06of66f7.execute-api.us-east-1.amazonaws.com/prod/send-booking-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData)
      });
      
      const responseText = await response.text();
      console.log('📧 Direct API Response:', { status: response.status, body: responseText });
      
      if (response.ok) {
        return { success: true, message: 'Completion email sent successfully', response: responseText };
      } else {
        throw new Error(`API failed: ${responseText}`);
      }
    } catch (apiError) {
      console.error('📧 Email API failed:', apiError);
      await logFailedEmail(recipientEmail, `Completion Email API Error: ${apiError?.message || 'Unknown API error'}`, appointmentDetails.id);
      
      return { success: true, message: 'Completion email queued for manual processing' };
    }
  } catch (error) {
    console.error('Completion email failed:', error);
    await logFailedEmail(recipientEmail, `Completion Email Error: ${error?.message || 'Unknown error'}`, appointmentDetails?.id);
    return { success: false, error: 'EMAIL_SEND_FAILED', message: error.message };
  }
};

// Create completion email template
const createCompletionEmailTemplate = (appointmentDetails, adminEmail) => {
  const tokenNumber = appointmentDetails?.tokenNumber || 'N/A';
  const siteCode = appointmentDetails?.siteCode || 'Photo Studio';
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        body { font-family: Arial, sans-serif; margin: 0; padding: 20px; background-color: #f5f5f5; }
        .container { max-width: 600px; margin: 0 auto; background-color: white; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
        .header { background-color: #232f3e; color: white; padding: 20px; text-align: center; }
        .content { padding: 30px; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>📸 Amazon Photo Studio</h1>
        </div>
        <div class="content">
          <h2>✅ Photo Session Complete!</h2>
          <p>Dear Customer,</p>
          <p>Your photo session with token <strong>${tokenNumber}</strong> at <strong>${siteCode}</strong> has been completed successfully.</p>
          <p>Thank you for choosing Amazon Photo Studio!</p>
        </div>
      </div>
    </body>
    </html>
  `;
};